import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { getWorkOrders, getSchedule, getParts } from '../../services/erpApi';
import { DEFAULT_COMPANY } from '../../constants/company';

const CATALOG_API = '/machinesim/api';

const STATUS_ORDER = { ACTIVE: 0, RELEASED: 1, 'NOT RELEASED': 2, COMPLETED: 3, CANCELLED: 4 };

const STATUS_STYLE = {
  ACTIVE: { color: 'var(--cn-warning)', border: 'var(--cn-warning)' },
  STARVED:     { color: 'var(--cn-warning)', border: 'var(--cn-warning)' },
  RELEASED:    { color: 'var(--cn-info)',    border: 'var(--cn-info)'    },
  'NOT RELEASED':     { color: 'var(--cn-muted)',   border: 'var(--cn-muted)'   },
  COMPLETED:    { color: 'var(--cn-success)', border: 'var(--cn-success)' },
  CANCELLED:   { color: 'var(--cn-danger)',  border: 'var(--cn-danger)'  },
};

const TYPE_COLOR = { PRESS: 'var(--cn-press)', MILL: 'var(--cn-mill)', LATHE: 'var(--cn-lathe)' };

const FILTERS = ['ALL', 'ACTIVE', 'RELEASED', 'NOT RELEASED', 'COMPLETED'];

const drawerInputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(168,144,96,0.2)',
  borderRadius: 4,
  color: 'var(--cn-text)',
  fontSize: 13,
  padding: '8px 12px',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
};

const drawerLabelStyle = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--cn-dimmed)',
  marginBottom: 5,
  display: 'block',
};

function getMachineType(id) {
  if (!id) return null;
  if (id.startsWith('PRESS')) return 'PRESS';
  if (id.startsWith('MILL'))  return 'MILL';
  if (id.startsWith('LATHE')) return 'LATHE';
  return null;
}

function formatEttc(op) {
  if (op.status === 'COMPLETED') return '—';
  let secs;
  if (op.status === 'ACTIVE') {
    secs = (op.targetQty - op.completedQty) * op.cycleTimeSec;
  } else {
    secs = (op.setupTimeSec || 0) + op.targetQty * op.cycleTimeSec;
  }
  if (secs <= 0) return '—';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function autoPartNumber(name) {
  return name.trim().toUpperCase().replace(/\s+/g, '-').replace(/[^A-Z0-9-]/g, '');
}

function machineDisplayName(machineId, companySlug) {
  if (companySlug && companySlug !== 'default' && machineId?.startsWith(`${companySlug}-`)) {
    return machineId.slice(companySlug.length + 1);
  }
  return machineId;
}

function woProgressQty(wo) {
  const ops = wo.workOrderOperations || [];
  if (!ops.length) return wo.completedQty ?? 0;
  return Math.min(...ops.map(o => o.completedQty ?? 0));
}

// ─── Root ────────────────────────────────────────────────────────────────────

export default function ERPPage() {
  const [searchParams] = useSearchParams();
  const companySlug = searchParams.get('company') || DEFAULT_COMPANY;

  const [tab, setTab] = useState('workorders');

  // Work Orders state
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('ALL');
  const [selectedId, setSelectedId] = useState(null);
  const [ettcMap, setEttcMap]       = useState({});

  // Parts state
  const [parts, setParts]           = useState([]);
  const [materials, setMaterials]   = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  // null | { mode: 'detail', part } | { mode: 'create' }
  const [partDrawer, setPartDrawer] = useState(null);

  const switchTab = (t) => {
    setTab(t);
    setSelectedId(null);
    setPartDrawer(null);
  };

  // ── Work Orders polling ───────────────────────────────────────────
  const loadWorkOrders = async () => {
    try {
      const filters = filter !== 'ALL' ? { status: filter, company: companySlug } : { company: companySlug };
      const data = await getWorkOrders(filters);
      data.sort((a, b) => (STATUS_ORDER[a.status] ?? 5) - (STATUS_ORDER[b.status] ?? 5));
      setWorkOrders(data);
    } catch {
      // keep previous data on error
    } finally {
      setLoading(false);
    }
  };

  const loadSchedule = async () => {
    try {
      const data = await getSchedule();
      const map = {};
      for (const machine of data.machines || []) {
        for (const block of machine.blocks || []) {
          const cur = map[block.woNumber];
          if (!cur || block.schedEnd > cur) map[block.woNumber] = block.schedEnd;
        }
      }
      setEttcMap(map);
    } catch {
      // keep previous data on error
    }
  };

  useEffect(() => {
    loadWorkOrders();
    const id = setInterval(loadWorkOrders, 3000);
    return () => clearInterval(id);
  }, [filter, companySlug]);

  useEffect(() => {
    loadSchedule();
    const id = setInterval(loadSchedule, 15000);
    return () => clearInterval(id);
  }, []);

  // ── Parts loading ─────────────────────────────────────────────────
  const loadParts = async () => {
    setPartsLoading(true);
    try {
      const [parts, mRes] = await Promise.all([
        getParts(companySlug),
        axios.get(`${CATALOG_API}/materials`),
      ]);
      setParts(parts);
      setMaterials(mRes.data.materials);
    } catch {
      // keep previous data on error
    } finally {
      setPartsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'parts') loadParts();
  }, [tab, companySlug]);

  // ── Derived ───────────────────────────────────────────────────────
  const inProgress = workOrders.filter((w) => w.status === 'ACTIVE').length;
  const released   = workOrders.filter((w) => w.status === 'RELEASED').length;
  const complete   = workOrders.filter((w) => w.status === 'COMPLETED').length;

  const selectedWo     = workOrders.find((w) => w.id === selectedId) ?? null;
  const woDrawerOpen   = tab === 'workorders' && selectedWo !== null;
  const partDrawerOpen = tab === 'parts' && partDrawer !== null;
  const anyDrawerOpen  = woDrawerOpen || partDrawerOpen;

  const toggleSelect = (id) => setSelectedId((prev) => (prev === id ? null : id));

  return (
    <div style={{ position: 'relative', minHeight: '100%' }}>
      <div style={{
        padding: '32px 36px',
        marginRight: anyDrawerOpen ? 440 : 0,
        transition: 'margin-right 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--cn-text)', margin: 0 }}>ERP</h1>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', marginBottom: 24, borderBottom: '1px solid rgba(168,144,96,0.1)' }}>
          {[
            { key: 'workorders', label: 'Work Orders' },
            { key: 'parts',      label: 'Parts' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => switchTab(key)}
              style={{
                padding: '8px 20px',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                background: 'transparent',
                border: 'none',
                borderBottom: tab === key ? '2px solid var(--cn-gold)' : '2px solid transparent',
                color: tab === key ? 'var(--cn-gold)' : 'var(--cn-muted)',
                cursor: 'pointer',
                marginBottom: -1,
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Work Orders tab ─────────────────────────────────────── */}
        {tab === 'workorders' && (
          <>
            {/* Summary chips */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
              <SummaryChip label="In Progress" value={inProgress} color="var(--cn-warning)" />
              <SummaryChip label="Released"    value={released}   color="var(--cn-info)"    />
              <SummaryChip label="Complete"    value={complete}   color="var(--cn-success)" />
              <div style={{ flex: 1 }} />
              <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', alignSelf: 'center' }}>Auto-refresh 3s</div>
            </div>

            {/* Filter bar */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    padding: '6px 14px',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    borderRadius: 4,
                    border: filter === f ? '1px solid var(--cn-gold)' : '1px solid rgba(168,144,96,0.15)',
                    background: filter === f ? 'rgba(168,144,96,0.12)' : 'transparent',
                    color: filter === f ? 'var(--cn-gold)' : 'var(--cn-muted)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {f === 'ALL' ? 'All' : f.replace('_', ' ')}
                </button>
              ))}
            </div>

            {/* WO Table */}
            {loading ? (
              <div style={{ color: 'var(--cn-muted)', padding: 24 }}>Loading…</div>
            ) : (
              <div style={{
                background: 'var(--cn-surface)',
                border: '1px solid rgba(168,144,96,0.1)',
                borderRadius: 8,
                overflow: 'hidden',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--cn-surface2)', borderBottom: '1px solid rgba(168,144,96,0.15)' }}>
                      {['WO #', 'Status', 'Part', 'Progress', 'Est. Complete', 'Due Date'].map((h) => (
                        <th key={h} style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: 'var(--cn-muted)',
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {workOrders.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--cn-dimmed)' }}>
                          No work orders found
                        </td>
                      </tr>
                    ) : (
                      workOrders.map((wo, idx) => {
                        const s = STATUS_STYLE[wo.status] || STATUS_STYLE.PLANNED;
                        const pct = wo.quantity > 0 ? Math.round((woProgressQty(wo) / wo.quantity) * 100) : 0;
                        const isSelected = selectedId === wo.id;
                        const isLast = idx === workOrders.length - 1;

                        return (
                          <tr
                            key={wo.id}
                            onClick={() => toggleSelect(wo.id)}
                            style={{
                              borderBottom: !isLast ? '1px solid rgba(168,144,96,0.06)' : 'none',
                              borderLeft: isSelected ? '2px solid var(--cn-gold)' : '2px solid transparent',
                              cursor: 'pointer',
                              background: isSelected ? 'rgba(168,144,96,0.06)' : 'transparent',
                              transition: 'background 0.1s, border-left-color 0.1s',
                            }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                            onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                          >
                            <td style={{ padding: '11px 16px', fontWeight: 600, color: 'var(--cn-text)', fontSize: 12 }}>
                              <span style={{ marginRight: 8, color: isSelected ? 'var(--cn-gold)' : 'var(--cn-dimmed)', fontSize: 10 }}>
                                {isSelected ? '◈' : '◇'}
                              </span>
                              {wo.woNumber}
                            </td>
                            <td style={{ padding: '11px 16px' }}>
                              <span style={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                padding: '3px 8px',
                                borderRadius: 999,
                                border: `1px solid ${s.border}`,
                                color: s.color,
                                background: `${s.color}18`,
                                textTransform: 'uppercase',
                              }}>
                                {wo.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td style={{ padding: '11px 16px' }}>
                              <div style={{ color: 'var(--cn-gold)', fontSize: 12, fontWeight: 600 }}>{wo.part?.partNumber ?? '—'}</div>
                              <div style={{ color: 'var(--cn-muted)', fontSize: 11, marginTop: 1 }}>{wo.part?.name ?? ''}</div>
                            </td>
                            <td style={{ padding: '11px 16px', minWidth: 140 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
                                  <div style={{
                                    height: '100%',
                                    width: `${pct}%`,
                                    background: wo.status === 'COMPLETED' ? 'var(--cn-success)' : 'var(--cn-gold)',
                                    borderRadius: 2,
                                    transition: 'width 0.4s',
                                  }} />
                                </div>
                                <span style={{ fontSize: 11, color: 'var(--cn-muted)', minWidth: 36, textAlign: 'right' }}>
                                  {woProgressQty(wo)}/{wo.quantity}
                                </span>
                              </div>
                            </td>
                            <td style={{ padding: '11px 16px', fontSize: 12, whiteSpace: 'nowrap' }}>
                              {(() => {
                                const iso = ettcMap[wo.woNumber];
                                if (!iso) return <span style={{ color: 'var(--cn-dimmed)' }}>—</span>;
                                const d = new Date(iso);
                                const isPast = d <= new Date();
                                // A past estimated-completion on a not-yet-complete WO is OVERDUE,
                                // not "done" — don't paint it success-green (Cognitive Bias: color
                                // semantics). Warning-amber for overdue; muted for a completed WO.
                                const overdue = isPast && wo.status !== 'COMPLETED';
                                return (
                                  <span title={overdue ? 'Past estimated completion — running late' : undefined}
                                    style={{ color: overdue ? 'var(--cn-warning)' : isPast ? 'var(--cn-muted)' : 'var(--cn-text)', fontWeight: 500 }}>
                                    {d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}
                                    {overdue && ' ⚠'}
                                  </span>
                                );
                              })()}
                            </td>
                            <td style={{ padding: '11px 16px', color: 'var(--cn-muted)', fontSize: 12 }}>
                              {wo.dueDate ? new Date(wo.dueDate).toLocaleDateString() : '—'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── Parts tab ───────────────────────────────────────────── */}
        {tab === 'parts' && (
          <PartCatalogSection
            parts={parts}
            loading={partsLoading}
            partDrawer={partDrawer}
            setPartDrawer={setPartDrawer}
          />
        )}
      </div>

      {/* WO Drawer */}
      <WoDrawer wo={selectedWo} open={woDrawerOpen} companySlug={companySlug} onClose={() => setSelectedId(null)} />

      {/* Part Drawer */}
      <PartDrawer
        open={partDrawerOpen}
        drawer={partDrawer}
        materials={materials}
        companySlug={companySlug}
        onClose={() => setPartDrawer(null)}
        onCreated={() => { setPartDrawer(null); loadParts(); }}
      />
    </div>
  );
}

// ─── Part Catalog Section ────────────────────────────────────────────────────

function PartCatalogSection({ parts, loading, partDrawer, setPartDrawer }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q
    ? parts.filter((p) => (p.partNumber || '').toLowerCase().includes(q) || (p.name || '').toLowerCase().includes(q))
    : parts;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <SummaryChip label="Parts" value={parts.length} color="var(--cn-gold)" />
        {/* Search — the catalog was an unfilterable scroll (Work Orders already filters);
            finding one part meant scanning every row (Hick / Choice Overload). */}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search part # or name…"
          style={{ ...drawerInputStyle, maxWidth: 240 }}
        />
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setPartDrawer({ mode: 'create' })}
          style={{
            padding: '8px 16px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderRadius: 4,
            border: '1px solid rgba(168,144,96,0.25)',
            background: 'rgba(168,144,96,0.1)',
            color: 'var(--cn-gold)',
            cursor: 'pointer',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(168,144,96,0.18)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(168,144,96,0.1)'}
        >
          + New Part
        </button>
      </div>

      {loading ? (
        <div style={{ color: 'var(--cn-muted)', padding: 24 }}>Loading…</div>
      ) : (
        <div style={{
          background: 'var(--cn-surface)',
          border: '1px solid rgba(168,144,96,0.1)',
          borderRadius: 8,
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--cn-surface2)', borderBottom: '1px solid rgba(168,144,96,0.15)' }}>
                {['Part #', 'Name', 'Material', 'Operations'].map((h) => (
                  <th key={h} style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--cn-muted)',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: 32, textAlign: 'center', color: 'var(--cn-dimmed)' }}>
                    {q ? (
                      <>No parts match “{query}”.</>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <span>No parts yet.</span>
                        <button onClick={() => setPartDrawer({ mode: 'create' })}
                          style={{ padding: '8px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', borderRadius: 4, border: '1px solid rgba(168,144,96,0.25)', background: 'rgba(168,144,96,0.1)', color: 'var(--cn-gold)', cursor: 'pointer' }}>
                          + New Part
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                shown.map((part, idx) => {
                  const isSelected = partDrawer?.mode === 'detail' && partDrawer?.part?.id === part.id;
                  const isLast = idx === shown.length - 1;
                  return (
                    <tr
                      key={part.id}
                      onClick={() => setPartDrawer(isSelected ? null : { mode: 'detail', part })}
                      style={{
                        borderBottom: !isLast ? '1px solid rgba(168,144,96,0.06)' : 'none',
                        borderLeft: isSelected ? '2px solid var(--cn-gold)' : '2px solid transparent',
                        cursor: 'pointer',
                        background: isSelected ? 'rgba(168,144,96,0.06)' : 'transparent',
                        transition: 'background 0.1s, border-left-color 0.1s',
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ padding: '11px 16px', fontWeight: 700, color: 'var(--cn-gold)', fontSize: 12, fontFamily: '"Courier New", monospace' }}>
                        <span style={{ marginRight: 8, color: isSelected ? 'var(--cn-gold)' : 'var(--cn-dimmed)', fontSize: 10 }}>
                          {isSelected ? '◈' : '◇'}
                        </span>
                        {part.partNumber}
                      </td>
                      <td style={{ padding: '11px 16px', color: 'var(--cn-text)', fontSize: 12 }}>{part.name}</td>
                      <td style={{ padding: '11px 16px', fontSize: 12 }}>
                        {part.material
                          ? <span style={{ color: 'var(--cn-muted)' }}>{part.material.name}</span>
                          : <span style={{ color: 'var(--cn-dimmed)' }}>—</span>}
                      </td>
                      <td style={{ padding: '11px 16px', color: 'var(--cn-dimmed)', fontSize: 12 }}>
                        {part.operations?.length ?? 0}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Part Drawer ─────────────────────────────────────────────────────────────

function PartDrawer({ open, drawer, materials, companySlug, onClose, onCreated }) {
  const [form, setForm]     = useState({ name: '', partNumber: '', materialId: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [autoPN, setAutoPN] = useState(true);

  // Part Base flow state
  const [partBases, setPartBases]         = useState([]);
  const [basesLoading, setBasesLoading]   = useState(false);
  const [selectedBase, setSelectedBase]   = useState(null);
  const [dimValues, setDimValues]         = useState({});   // { [key]: value }
  const [cyclePreview, setCyclePreview]   = useState(null); // array of { sequence, machineType, cycleTime, setupTime }
  const [computing, setComputing]         = useState(false);
  const [computeError, setComputeError]   = useState('');

  // Fetch part bases once when create drawer opens
  useEffect(() => {
    if (drawer?.mode === 'create' && open) {
      setForm({ name: '', partNumber: '', materialId: '' });
      setAutoPN(true);
      setError('');
      setSelectedBase(null);
      setDimValues({});
      setCyclePreview(null);
      setBasesLoading(true);
      axios.get(`${CATALOG_API}/part-bases`)
        .then((res) => setPartBases(res.data.partBases))
        .catch(() => setPartBases([]))
        .finally(() => setBasesLoading(false));
    }
  }, [drawer?.mode, open]);

  // Call /compute whenever base, material, or dimensions change
  useEffect(() => {
    if (!selectedBase || !form.materialId) { setCyclePreview(null); return; }
    const dims = selectedBase.dimensions || [];
    const allFilled = dims.every(d => dimValues[d.key] !== undefined && dimValues[d.key] !== '');
    if (!allFilled && dims.length > 0) { setCyclePreview(null); return; }

    const dimensions = Object.fromEntries(dims.map(d => [d.key, Number(dimValues[d.key] ?? d.defaultValue)]));

    let cancelled = false;
    setComputing(true);
    setComputeError('');
    axios.post(`${CATALOG_API}/part-bases/${selectedBase.id}/compute`, {
      materialId: form.materialId,
      dimensions,
    })
      .then(res => { if (!cancelled) setCyclePreview(res.data.operations); })
      .catch(err => { if (!cancelled) setComputeError(err.response?.data?.error || 'Failed to compute cycle times'); })
      .finally(() => { if (!cancelled) setComputing(false); });

    return () => { cancelled = true; };
  }, [selectedBase?.id, dimValues, form.materialId]);

  const handleNameChange = (val) => {
    setForm(f => ({
      ...f,
      name: val,
      ...(autoPN ? { partNumber: autoPartNumber(val) } : {}),
    }));
  };

  const handlePartNumberChange = (val) => {
    setAutoPN(false);
    setForm(f => ({ ...f, partNumber: val }));
  };

  const submit = async () => {
    if (!form.name.trim() || !form.partNumber.trim() || !selectedBase) return;
    setSaving(true);
    setError('');
    try {
      const dimensions = {};
      (selectedBase.dimensions || []).forEach((d) => {
        dimensions[d.key] = Number(dimValues[d.key] ?? d.defaultValue);
      });
      await axios.post(`${CATALOG_API}/parts?company=${companySlug}`, {
        name: form.name.trim(),
        partNumber: form.partNumber.trim(),
        partBaseId: selectedBase.id,
        ...(form.materialId ? { materialId: form.materialId } : {}),
        ...((selectedBase.dimensions || []).length > 0 ? { dimensions } : {}),
      });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create part');
    } finally {
      setSaving(false);
    }
  };

  const part = drawer?.part;

  return (
    <div style={{
      position: 'fixed',
      right: 0,
      top: 0,
      height: '100vh',
      width: 440,
      background: 'var(--cn-surface)',
      borderLeft: '1px solid rgba(168,144,96,0.15)',
      boxShadow: '-12px 0 40px rgba(0,0,0,0.35)',
      transform: open ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
      zIndex: 50,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Collapse tab */}
      <button
        onClick={onClose}
        title="Collapse"
        style={{
          position: 'absolute',
          left: -30,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 30,
          height: 80,
          background: 'var(--cn-surface)',
          border: '1px solid rgba(168,144,96,0.15)',
          borderRight: 'none',
          borderRadius: '6px 0 0 6px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--cn-muted)',
          fontSize: 16,
          boxShadow: '-4px 0 12px rgba(0,0,0,0.2)',
          transition: 'color 0.15s, background 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(168,144,96,0.1)';
          e.currentTarget.style.color = 'var(--cn-gold)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--cn-surface)';
          e.currentTarget.style.color = 'var(--cn-muted)';
        }}
      >
        ›
      </button>

      {/* Drawer header */}
      <div style={{
        padding: '20px 20px 16px',
        borderBottom: '1px solid rgba(168,144,96,0.1)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            {drawer?.mode === 'create' ? (
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--cn-text)' }}>New Part</div>
            ) : (
              <>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--cn-gold)', marginBottom: 4, fontFamily: '"Courier New", monospace' }}>
                  {part?.partNumber}
                </div>
                <div style={{ fontSize: 13, color: 'var(--cn-muted)' }}>{part?.name}</div>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--cn-dimmed)', cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cn-text)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--cn-dimmed)'}
          >
            ✕
          </button>
        </div>
        {drawer?.mode === 'detail' && part?.material && (
          <div style={{ marginTop: 10 }}>
            <MetaItem label="Material" value={part.material.name} />
          </div>
        )}
      </div>

      {/* Drawer body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
        {drawer?.mode === 'create' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Step 1 — Base picker */}
            <div>
              <label style={drawerLabelStyle}>Part Base</label>
              {basesLoading ? (
                <div style={{ fontSize: 12, color: 'var(--cn-muted)' }}>Loading bases…</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {partBases.map((base) => {
                    const isSelected = selectedBase?.id === base.id;
                    const opLine = (base.operations || []).map((o) => o.machineType || o.type || o).join(' → ');
                    return (
                      <div
                        key={base.id}
                        onClick={() => {
                          setSelectedBase(base);
                          setDimValues({});
                          setCyclePreview(null);
                        }}
                        style={{
                          padding: '10px 12px',
                          borderRadius: 5,
                          border: isSelected
                            ? '1px solid var(--cn-gold)'
                            : '1px solid rgba(168,144,96,0.15)',
                          background: isSelected ? 'rgba(168,144,96,0.1)' : 'rgba(255,255,255,0.02)',
                          cursor: 'pointer',
                          transition: 'border-color 0.15s, background 0.15s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <div style={{
                          width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                          border: isSelected ? '4px solid var(--cn-gold)' : '2px solid rgba(168,144,96,0.4)',
                          background: isSelected ? 'var(--cn-gold)' : 'transparent',
                          transition: 'all 0.15s',
                        }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? 'var(--cn-gold)' : 'var(--cn-text)' }}>
                            {base.name}
                          </div>
                          {opLine && (
                            <div style={{ fontSize: 10, color: 'var(--cn-dimmed)', marginTop: 2, letterSpacing: '0.04em' }}>
                              {opLine}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {partBases.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--cn-dimmed)' }}>No bases available</div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2 — Dimension inputs (skipped if base has no dimensions) */}
            {selectedBase && (selectedBase.dimensions || []).length > 0 && (
              <div>
                <label style={drawerLabelStyle}>Dimensions</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(selectedBase.dimensions || []).map((dim) => (
                    <div key={dim.key}>
                      <label style={{ ...drawerLabelStyle, marginBottom: 3 }}>
                        {dim.label}{dim.unit ? ` (${dim.unit})` : ''}
                      </label>
                      <input
                        type="number"
                        value={dimValues[dim.key] !== undefined ? dimValues[dim.key] : dim.defaultValue}
                        min={dim.min}
                        max={dim.max}
                        step="any"
                        onChange={(e) => setDimValues(dv => ({ ...dv, [dim.key]: e.target.value }))}
                        style={drawerInputStyle}
                      />
                      {(dim.min !== undefined || dim.max !== undefined) && (
                        <div style={{ fontSize: 10, color: 'var(--cn-dimmed)', marginTop: 3 }}>
                          {dim.min !== undefined && `min ${dim.min}`}
                          {dim.min !== undefined && dim.max !== undefined && ' · '}
                          {dim.max !== undefined && `max ${dim.max}`}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3 — Material dropdown */}
            {selectedBase && (
              <div>
                <label style={drawerLabelStyle}>Material</label>
                <select
                  value={form.materialId}
                  onChange={(e) => setForm(f => ({ ...f, materialId: e.target.value }))}
                  style={{ ...drawerInputStyle, cursor: 'pointer' }}
                >
                  <option value="">— none —</option>
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Step 4 — Live cycle time preview */}
            {selectedBase && form.materialId && (
              <div>
                <label style={drawerLabelStyle}>Cycle Time Preview</label>
                {computing ? (
                  <div style={{ fontSize: 12, color: 'var(--cn-muted)' }}>Computing…</div>
                ) : computeError ? (
                  <div style={{
                    fontSize: 12, color: 'var(--cn-danger)', padding: '8px 12px',
                    background: 'rgba(192,57,43,0.1)', borderRadius: 4,
                    border: '1px solid rgba(192,57,43,0.2)',
                  }}>
                    {computeError}
                  </div>
                ) : cyclePreview ? (
                  <div style={{
                    border: '1px solid rgba(168,144,96,0.12)',
                    borderRadius: 5,
                    overflow: 'hidden',
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(168,144,96,0.1)' }}>
                          {['Seq', 'Machine Type', 'Cycle (s)', 'Setup (s)'].map((h) => (
                            <th key={h} style={{
                              padding: '6px 10px', textAlign: 'left',
                              fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                              textTransform: 'uppercase', color: 'var(--cn-dimmed)',
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {cyclePreview.map((op, i) => {
                          const tc = TYPE_COLOR[op.machineType] || TYPE_COLOR[op.type] || 'var(--cn-muted)';
                          return (
                            <tr key={i} style={{ borderBottom: i < cyclePreview.length - 1 ? '1px solid rgba(168,144,96,0.06)' : 'none' }}>
                              <td style={{ padding: '7px 10px', color: 'var(--cn-dimmed)', fontSize: 11 }}>
                                {op.sequence ?? (i + 1) * 10}
                              </td>
                              <td style={{ padding: '7px 10px' }}>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, color: tc,
                                  padding: '2px 7px', background: `${tc}18`,
                                  borderRadius: 3, textTransform: 'uppercase',
                                }}>
                                  {op.machineType || op.type || '—'}
                                </span>
                              </td>
                              <td style={{ padding: '7px 10px', fontWeight: 600, color: 'var(--cn-text)', fontFamily: '"Courier New", monospace' }}>
                                {op.cycleTime !== null && op.cycleTime !== undefined ? op.cycleTime : '—'}
                              </td>
                              <td style={{ padding: '7px 10px', color: 'var(--cn-muted)', fontFamily: '"Courier New", monospace' }}>
                                {op.setupTime !== null && op.setupTime !== undefined ? op.setupTime : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (selectedBase.dimensions || []).length > 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--cn-dimmed)' }}>
                    Fill in all dimensions to see cycle time preview
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--cn-dimmed)' }}>
                    No coefficient data available for this combination
                  </div>
                )}
              </div>
            )}

            {/* Step 5 — Name + part number */}
            {selectedBase && (
              <>
                <div>
                  <label style={drawerLabelStyle}>Part Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Spork Head Assembly"
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    style={drawerInputStyle}
                  />
                </div>

                <div>
                  <label style={drawerLabelStyle}>Part Number</label>
                  <input
                    type="text"
                    placeholder="e.g. SPORK-HEAD"
                    value={form.partNumber}
                    onChange={(e) => handlePartNumberChange(e.target.value)}
                    style={{ ...drawerInputStyle, fontFamily: '"Courier New", monospace', letterSpacing: '0.04em' }}
                  />
                  <div style={{ fontSize: 10, color: 'var(--cn-dimmed)', marginTop: 4 }}>
                    Auto-generated from name · edit to override
                  </div>
                </div>
              </>
            )}

            {/* Error */}
            {error && (
              <div style={{
                fontSize: 12, color: 'var(--cn-danger)', padding: '8px 12px',
                background: 'rgba(192,57,43,0.1)', borderRadius: 4,
                border: '1px solid rgba(192,57,43,0.2)',
              }}>
                {error}
              </div>
            )}

            {/* Tell the user which requirement is unmet instead of a silent disabled button
                (Cognitive Load / Postel — communicate constraints before the user fails). */}
            {!saving && (() => {
              const missing = !selectedBase ? 'Pick a base shape'
                : !form.materialId ? 'Select a material'
                : !form.name.trim() ? 'Add a part name'
                : !form.partNumber.trim() ? 'Add a part number' : null;
              return missing ? (
                <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', marginBottom: 2 }}>{missing} to create this part.</div>
              ) : null;
            })()}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 4, fontSize: 12,
                  fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                  cursor: 'pointer', background: 'rgba(168,144,96,0.1)',
                  color: 'var(--cn-gold)', border: '1px solid rgba(168,144,96,0.25)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving || !selectedBase || !form.materialId || !form.name.trim() || !form.partNumber.trim()}
                style={{
                  flex: 2, padding: '9px 0', borderRadius: 4, fontSize: 12,
                  fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                  cursor: (saving || !selectedBase || !form.materialId || !form.name.trim() || !form.partNumber.trim()) ? 'not-allowed' : 'pointer',
                  background: 'var(--cn-gold)', color: '#0D1B2A', border: 'none',
                  opacity: (saving || !selectedBase || !form.materialId || !form.name.trim() || !form.partNumber.trim()) ? 0.5 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                {saving ? 'Creating…' : 'Create Part'}
              </button>
            </div>
          </div>
        )}

        {drawer?.mode === 'detail' && part && (
          <div>
            {(part.operations || []).length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--cn-dimmed)', padding: '32px 0', fontSize: 13 }}>
                No operations found
              </div>
            ) : (
              (part.operations || []).map((op, i) => {
                const tc = TYPE_COLOR[op.machineType] || 'var(--cn-muted)';
                return (
                  <div
                    key={op.id}
                    style={{
                      padding: '14px 0',
                      borderBottom: i < part.operations.length - 1 ? '1px solid rgba(168,144,96,0.07)' : 'none',
                    }}
                  >
                    {/* Op header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 800, color: 'var(--cn-dimmed)',
                        background: 'rgba(255,255,255,0.05)', padding: '2px 7px',
                        borderRadius: 3, minWidth: 32, textAlign: 'center',
                      }}>
                        {op.sequence}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--cn-text)', flex: 1 }}>{op.name}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                        padding: '2px 8px', borderRadius: 999,
                        border: `1px solid ${tc}`, color: tc, background: `${tc}18`,
                        textTransform: 'uppercase',
                      }}>
                        {op.machineType}
                      </span>
                    </div>

                    {/* Timing */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: op.tools?.length > 0 ? 10 : 0 }}>
                      <MetaItem label="Cycle" value={`${op.cycleTime ?? '?'}s`} />
                      <MetaItem label="Setup" value={`${op.setupTime ?? '?'}s`} />
                    </div>

                    {/* Tool slots */}
                    {op.tools?.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {op.tools.map((t) => (
                          <div key={t.id} style={{
                            background: 'rgba(168,144,96,0.06)',
                            border: '1px solid rgba(168,144,96,0.15)',
                            borderRadius: 4, padding: '4px 10px', fontSize: 11,
                          }}>
                            <span style={{ fontFamily: '"Courier New", monospace', color: 'var(--cn-gold)', fontWeight: 700 }}>
                              T:{t.toolKey}
                            </span>
                            <span style={{ color: 'var(--cn-dimmed)', marginLeft: 6 }}>{t.cyclePercentage}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── WO Drawer ───────────────────────────────────────────────────────────────

function WoDrawer({ wo, open, companySlug, onClose }) {
  return (
    <div style={{
      position: 'fixed',
      right: 0,
      top: 0,
      height: '100vh',
      width: 440,
      background: 'var(--cn-surface)',
      borderLeft: '1px solid rgba(168,144,96,0.15)',
      boxShadow: '-12px 0 40px rgba(0,0,0,0.35)',
      transform: open ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
      zIndex: 50,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Collapse tab — sticks out on the left edge */}
      <button
        onClick={onClose}
        title="Collapse"
        style={{
          position: 'absolute',
          left: -30,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 30,
          height: 80,
          background: 'var(--cn-surface)',
          border: '1px solid rgba(168,144,96,0.15)',
          borderRight: 'none',
          borderRadius: '6px 0 0 6px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--cn-muted)',
          fontSize: 16,
          boxShadow: '-4px 0 12px rgba(0,0,0,0.2)',
          transition: 'color 0.15s, background 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(168,144,96,0.1)';
          e.currentTarget.style.color = 'var(--cn-gold)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--cn-surface)';
          e.currentTarget.style.color = 'var(--cn-muted)';
        }}
      >
        ›
      </button>

      {/* Drawer header */}
      <div style={{
        padding: '20px 20px 16px',
        borderBottom: '1px solid rgba(168,144,96,0.1)',
        flexShrink: 0,
      }}>
        {wo ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--cn-text)', marginBottom: 4 }}>
                  {wo.woNumber}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cn-gold)' }}>
                    {wo.part?.partNumber}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--cn-muted)' }}>
                    {wo.part?.name}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--cn-dimmed)',
                  cursor: 'pointer',
                  fontSize: 18,
                  padding: '0 4px',
                  lineHeight: 1,
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--cn-text)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--cn-dimmed)'}
              >
                ✕
              </button>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 16 }}>
              <MetaItem label="Due" value={wo.dueDate ? new Date(wo.dueDate).toLocaleDateString() : '—'} />
              <MetaItem label="Qty" value={`${woProgressQty(wo)} / ${wo.quantity}`} />
              <MetaItem
                label="Status"
                value={wo.status.replace('_', ' ')}
                color={(STATUS_STYLE[wo.status] || STATUS_STYLE.PLANNED).color}
              />
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--cn-muted)' }}>No work order selected</div>
        )}
      </div>

      {/* Operations list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
        {wo && (wo.workOrderOperations || []).length === 0 && (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--cn-dimmed)', fontSize: 13 }}>
            No operations found
          </div>
        )}
        {wo && (wo.workOrderOperations || []).map((op, i) => {
          const os  = STATUS_STYLE[op.status] || STATUS_STYLE.PLANNED;
          const pct = op.targetQty > 0 ? Math.round((op.completedQty / op.targetQty) * 100) : 0;
          const mt  = op.machine?.machineType || getMachineType(op.assignedSimMachineId);
          const typeColor = mt ? TYPE_COLOR[mt] : 'var(--cn-muted)';
          const machineLabel = machineDisplayName(op.assignedSimMachineId, companySlug);
          const live = op.live || {};

          return (
            <div
              key={op.id}
              style={{
                padding: '14px 20px',
                borderBottom: i < (wo.workOrderOperations.length - 1) ? '1px solid rgba(168,144,96,0.07)' : 'none',
              }}
            >
              {/* Op header row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: 'var(--cn-dimmed)',
                  background: 'rgba(255,255,255,0.05)',
                  padding: '2px 7px',
                  borderRadius: 3,
                  minWidth: 32,
                  textAlign: 'center',
                }}>
                  {op.sequence}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--cn-text)', flex: 1 }}>{op.name}</span>
                {live.isActive && (
                  <span title="Machine actively running this op" style={{ width: 8, height: 8, borderRadius: '50%', border: '2px solid var(--cn-warning)', borderTopColor: 'transparent', animation: 'erpSpin 0.8s linear infinite', flexShrink: 0 }} />
                )}
                <span style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  padding: '2px 8px',
                  borderRadius: 999,
                  border: `1px solid ${os.border}`,
                  color: os.color,
                  background: `${os.color}18`,
                  textTransform: 'uppercase',
                }}>
                  {op.status.replace('_', ' ')}
                </span>
              </div>

              {live.drift && (
                <div style={{ fontSize: 10, color: 'var(--cn-warning)', marginBottom: 8, padding: '4px 8px', background: 'rgba(255,193,7,0.08)', borderRadius: 4 }}>
                  Status drift — op marked ACTIVE but machine is idle. Use Factory Reset in Settings.
                </div>
              )}

              {/* Machine + ETTC row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: typeColor, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: typeColor }}>
                    {machineLabel || '—'}
                  </span>
                </div>
                <div style={{ flex: 1 }} />
                {op.status !== 'COMPLETED' && (
                  <div style={{ fontSize: 11, color: 'var(--cn-muted)' }}>
                    ETTC <span style={{ color: 'var(--cn-text)', fontWeight: 600 }}>{formatEttc(op)}</span>
                  </div>
                )}
                <span style={{ fontSize: 11, color: 'var(--cn-muted)' }}>
                  {op.completedQty}/{op.targetQty}
                </span>
              </div>

              {/* Progress bar */}
              <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <div style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: os.color,
                  borderRadius: 2,
                  transition: 'width 0.4s',
                }} />
              </div>
            </div>
          );
        })}
      </div>
      <style>{`@keyframes erpSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function MetaItem({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--cn-dimmed)', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: color || 'var(--cn-text)' }}>{value}</div>
    </div>
  );
}

function SummaryChip({ label, value, color }) {
  return (
    <div style={{
      padding: '8px 16px',
      background: 'var(--cn-surface)',
      border: '1px solid rgba(168,144,96,0.1)',
      borderRadius: 6,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      <div style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
      <span style={{ fontSize: 16, fontWeight: 700, color, minWidth: 20, textAlign: 'right' }}>{value}</span>
      <span style={{ fontSize: 11, color: 'var(--cn-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
    </div>
  );
}
